import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    const cleanEmail = email.trim().toLowerCase();
    const cleanPass = password;
    if (!cleanEmail || !cleanPass) {
      setErr("Please enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      await login(cleanEmail, cleanPass);
      navigate("/", { replace: true });
    } catch (err) {
      const status = err?.status;
      const raw = err?.message || "";
      if (status === 401) {
        setErr("Wrong email or password. Please try again.");
      } else if (status === 0 || err?.networkError || /failed to fetch|network|cors|disturbed|locked/i.test(raw)) {
        setErr("Can't reach the server. Please check your connection and try again.");
      } else if (/invalid|unauthor|credential|password/i.test(raw)) {
        setErr("Wrong email or password. Please try again.");
      } else {
        setErr(raw || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-no-tab" style={{ paddingTop: 120 }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 14, marginBottom: 0,
      }}>
        <h1 style={{
          fontSize: 38, fontWeight: 800, letterSpacing: -1.2, margin: 0, lineHeight: 1.05,
          color: "var(--navy)", flex: 1, minWidth: 0,
        }}>
          Welcome <span style={{ color: "var(--primary-dark)" }}>back.</span>
        </h1>
        <img
          src="/mrscashback-icon.png"
          alt="Mrscashback"
          data-testid="login-logo"
          style={{
            width: 72, height: 72, objectFit: "contain", display: "block", flexShrink: 0,
          }}
        />
      </div>
      <p style={{ color: "var(--text-secondary)", fontSize: 15, marginTop: 2, marginBottom: 28 }}>
        Sign in to keep earning on every receipt.
      </p>

      {err && (
        <div
          data-testid="login-error"
          role="alert"
          style={{
            display: "flex", alignItems: "flex-start", gap: 10,
            background: "rgba(217, 72, 72, 0.08)",
            border: "1px solid rgba(217, 72, 72, 0.45)",
            borderRadius: 14,
            padding: "12px 14px",
            marginBottom: 16,
            color: "var(--error)",
            fontSize: 14, fontWeight: 600, lineHeight: 1.35,
          }}
        >
          <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{err}</span>
        </div>
      )}

      <form onSubmit={onSubmit} noValidate>
        <div style={{ marginBottom: 16 }}>
          <label className="label">Email</label>
          <input
            data-testid="login-email-input"
            type="email"
            inputMode="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="email"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          <label className="label">Password</label>
          <input
            data-testid="login-password-input"
            type="password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="current-password"
          />
        </div>

        <button
          data-testid="login-submit-button"
          className="btn-primary"
          type="submit"
          disabled={loading}
          style={{ width: "100%", marginTop: 8 }}
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>

      <div style={{ display: "flex", justifyContent: "center", marginTop: 28, color: "var(--text-secondary)" }}>
        <span>New here?&nbsp;&nbsp;</span>
        <Link to="/register" data-testid="goto-register-link" style={{ color: "var(--burgundy)", fontWeight: 700 }}>
          Create an account
        </Link>
      </div>
    </div>
  );
}
